import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminApi } from '../../services/api';
import toast from 'react-hot-toast';

const SuperAdminRestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Password management state
  const [showPassSection, setShowPassSection] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPass, setShowNewPass] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [qrUrl, setQrUrl] = useState('');

  useEffect(() => {
    if (data?.restaurant?.slug) {
      const url = process.env.REACT_APP_SITE_URL + '/' + data.restaurant.slug;
      QRCode.toDataURL(url, { width: 200, margin: 2 }).then(setQrUrl).catch(console.error);
    }
  }, [data]);

  useEffect(() => {
    superAdminApi.getRestaurantStats(id)
      .then(res => {
        setData(res.data.data);
        setEditForm({
          name: res.data.data.restaurant.name,
          email: res.data.data.restaurant.email || '',
          phone: res.data.data.restaurant.phone || '',
          address: res.data.data.restaurant.address || '',
          plan: res.data.data.restaurant.plan,
          is_active: res.data.data.restaurant.is_active,
        });
      })
      .catch(() => toast.error('Failed to load restaurant'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await superAdminApi.updateRestaurant(id, editForm);
      toast.success('Restaurant updated');
      setEditing(false);
      // Refresh
      const res = await superAdminApi.getRestaurantStats(id);
      setData(res.data.data);
    } catch {
      toast.error('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeAdminPassword = async () => {
    if (!selectedAdmin) return toast.error('Pehle admin select karo');
    if (!newPassword || newPassword.length < 6) return toast.error('Password kam se kam 6 characters ka hona chahiye');
    setChangingPass(true);
    try {
      await superAdminApi.resetAdminPassword(id, selectedAdmin.id, { new_password: newPassword });
      toast.success(`${selectedAdmin.name} ka password successfully change ho gaya!`);
      setNewPassword('');
      setSelectedAdmin(null);
      setShowPassSection(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Password change failed');
    } finally {
      setChangingPass(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', paddingTop: 60, color: '#6b7280' }}>Loading...</div>;
  if (!data) return <div style={{ textAlign: 'center', paddingTop: 60, color: '#ef4444' }}>Restaurant not found.</div>;

  const { restaurant, stats, staff, recentOrders } = data;

  const statBlock = (icon, label, value, color) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', border: '1px solid #ede9fe', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1f2937' }}>{value}</div>
      </div>
    </div>
  );

  const field = (label, key, type = 'text') => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      {key === 'plan' ? (
        <select value={editForm.plan} onChange={e => setEditForm(p => ({ ...p, plan: e.target.value }))}
          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
      ) : (
        <input type={type} value={editForm[key] ?? ''} onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
      )}
    </div>
  );

  return (
    <div>
      {/* Back */}
      <button onClick={() => navigate('/super-admin/restaurants')}
        style={{ background: 'none', border: 'none', color: '#7c3aed', fontSize: 14, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
        ← Back to Restaurants
      </button>

      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 14, padding: '24px 28px', border: '1px solid #ede9fe', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🏪</div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: 0 }}>{restaurant.name}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <code style={{ background: '#f5f3ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 6, fontSize: 13 }}>/{restaurant.slug}</code>
            </div>
            {qrUrl && (
              <div style={{ marginTop: 16, background: 'white', borderRadius: 16, padding: 20, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', border: '1px solid #ede9fe', textAlign: 'center', maxWidth: 220 }}>
                <p style={{ fontWeight: 700, fontSize: 13, color: '#7c3aed', marginBottom: 12, letterSpacing: 1, textTransform: 'uppercase' }}>📱 Scan to Visit</p>
                <div style={{ background: '#f5f3ff', borderRadius: 12, padding: 10, display: 'inline-block' }}>
                  <img src={qrUrl} alt="QR Code" style={{ width: 150, height: 150, display: 'block', borderRadius: 6 }} />
                </div>
                <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, marginBottom: 12, wordBreak: 'break-all' }}>{window.location.origin}/{restaurant.slug}</p>
                <a href={qrUrl} download={restaurant.slug + '-qr.png'} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 20px', background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: 'white', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>⬇️ Download QR</a>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                background: restaurant.is_active ? '#dcfce7' : '#fee2e2',
                color: restaurant.is_active ? '#166534' : '#991b1b',
              }}>{restaurant.is_active ? 'Active' : 'Disabled'}</span>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>{restaurant.plan}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a href={`/${restaurant.slug}`} target="_blank" rel="noopener noreferrer"
            style={{ padding: '9px 16px', background: '#f5f3ff', color: '#7c3aed', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}>
            View Site →
          </a>
          <button onClick={() => setEditing(p => !p)}
            style={{ padding: '9px 16px', background: editing ? '#fee2e2' : 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: editing ? '#991b1b' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            {editing ? 'Cancel' : '✏️ Edit'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
        <div>
          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            {statBlock('📦', 'Total Orders', stats.totalOrders.toLocaleString(), '#fef9c3')}
            {statBlock('💰', 'Total Revenue', `₹${stats.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, '#dcfce7')}
            {statBlock('🍽️', 'Menu Items', stats.activeMenuItems, '#f5f3ff')}
            {statBlock('📅', 'Reservations', stats.totalReservations, '#dbeafe')}
          </div>

          {/* Recent Orders */}
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Recent Orders</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                {['#', 'Customer', 'Amount', 'Status', 'Date'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {recentOrders.map((o, index) => (
                  <tr key={o.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#7c3aed' }}>#{index + 1}</td>
                    <td style={{ padding: '10px 12px', color: '#1f2937' }}>{o.customer_name}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>₹{parseFloat(o.total_amount).toFixed(0)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 99, fontWeight: 600,
                        background: o.status === 'delivered' ? '#dcfce7' : o.status === 'cancelled' ? '#fee2e2' : '#fef9c3',
                        color: o.status === 'delivered' ? '#166534' : o.status === 'cancelled' ? '#991b1b' : '#854d0e',
                      }}>{o.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#6b7280' }}>{new Date(o.created_at).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))}
                {recentOrders.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '24px 12px', textAlign: 'center', color: '#9ca3af' }}>No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right column: edit form + staff */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Edit Form */}
          {editing && (
            <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 16 }}>Edit Details</h2>
              {field('Name', 'name')}
              {field('Email', 'email', 'email')}
              {field('Phone', 'phone', 'tel')}
              {field('Address', 'address')}
              {field('Plan', 'plan')}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))} />
                  Active
                </label>
              </div>
              <button onClick={handleSave} disabled={saving}
                style={{ width: '100%', padding: 10, background: 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {/* Staff List */}
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 }}>Staff ({staff.length})</h2>
              <button
                onClick={() => { setShowPassSection(p => !p); setSelectedAdmin(null); setNewPassword(''); }}
                style={{ padding: '6px 12px', background: showPassSection ? '#fee2e2' : 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: showPassSection ? '#991b1b' : '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {showPassSection ? '✕ Cancel' : '🔑 Password Change'}
              </button>
            </div>
            {staff.map(s => (
              <div key={s.id}
                onClick={() => showPassSection && setSelectedAdmin(s)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 8px', borderBottom: '1px solid #f3f4f6', borderRadius: 8,
                  cursor: showPassSection ? 'pointer' : 'default',
                  background: showPassSection && selectedAdmin?.id === s.id ? '#f5f3ff' : 'transparent',
                  border: showPassSection && selectedAdmin?.id === s.id ? '1.5px solid #7c3aed' : '1.5px solid transparent',
                  transition: 'all 0.15s',
                }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{s.email}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 11, padding: '3px 8px', borderRadius: 99, fontWeight: 600,
                    background: s.role === 'admin' ? '#f5f3ff' : '#fef9c3',
                    color: s.role === 'admin' ? '#6d28d9' : '#92400e',
                  }}>{s.role}</span>
                  {showPassSection && selectedAdmin?.id === s.id && (
                    <span style={{ fontSize: 14 }}>✓</span>
                  )}
                </div>
              </div>
            ))}
            {staff.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No staff found.</p>}

            {/* Password change form */}
            {showPassSection && (
              <div style={{ marginTop: 16, padding: 16, background: '#f5f3ff', borderRadius: 10, border: '1px solid #ede9fe' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
                  {selectedAdmin ? `🔑 "${selectedAdmin.name}" ka naya password set karo` : '👆 Upar se admin select karo'}
                </div>
                {selectedAdmin && (
                  <>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, padding: '6px 10px', background: '#fff', borderRadius: 6 }}>
                      📧 {selectedAdmin.email} &nbsp;|&nbsp; 👤 {selectedAdmin.role}
                    </div>
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                      <input
                        type={showNewPass ? 'text' : 'password'}
                        placeholder="Naya password (min 6 chars)"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        style={{ width: '100%', padding: '9px 40px 9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                      />
                      <button
                        onClick={() => setShowNewPass(p => !p)}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280' }}>
                        {showNewPass ? '🙈' : '👁️'}
                      </button>
                    </div>
                    <button
                      onClick={handleChangeAdminPassword}
                      disabled={changingPass || !newPassword}
                      style={{ width: '100%', padding: 9, background: changingPass ? '#d1d5db' : 'linear-gradient(135deg,#7c3aed,#5b21b6)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: changingPass ? 'not-allowed' : 'pointer' }}>
                      {changingPass ? 'Changing...' : '🔒 Password Change Karo'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Restaurant info card */}
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #ede9fe' }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Info</h2>
            {[
              ['Email', restaurant.email],
              ['Phone', restaurant.phone],
              ['Address', restaurant.address],
              ['Created', new Date(restaurant.created_at).toLocaleDateString('en-IN')],
            ].map(([k, v]) => v && (
              <div key={k} style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{k}: </span>
                <span style={{ fontSize: 13, color: '#1f2937', fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminRestaurantDetail;
